import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Package, CheckCircle, Building2, Star, ShoppingCart, Check } from 'lucide-react';
import { formatCurrencySimple } from '../utils/formatters';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const EquipmentCard = ({ equipment }) => {
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const inCart = isInCart(equipment?.equipment_id);

  // Handle image URL - prepend backend URL for relative paths
  const getImageUrl = () => {
    const img = equipment?.image_url || equipment?.image;
    if (!img) return 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80';
    if (img.startsWith('/api')) return `${BACKEND_URL}${img}`;
    if (img.startsWith('http')) return img;
    return `${BACKEND_URL}${img}`;
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(equipment, 1);
  };

  return (
    <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 group border-gray-700 bg-gray-800 hover:border-yellow-500" data-testid={`equipment-card-${equipment?.equipment_id}`}>
      <div className="relative overflow-hidden h-56">
        <img
          src={getImageUrl()}
          alt={equipment?.name || 'Equipment'}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4">
          <Badge className="bg-blue-500 text-white font-semibold">
            {equipment?.category || 'Equipment'}
          </Badge>
        </div>
        {equipment?.quantity_available > 0 && (
          <div className="absolute top-4 left-4">
            <Badge className="bg-green-500 text-white">
              <CheckCircle className="h-3 w-3 mr-1" />
              {equipment.quantity_available} Available
            </Badge>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-xl text-white">{equipment?.name || 'Unnamed Equipment'}</CardTitle>
        <p className="text-gray-400 text-sm mt-2 line-clamp-2">
          {equipment?.description || 'Professional audio equipment for your events'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Business Info */}
        {equipment?.business && (
          <div className="flex items-center gap-2 text-sm text-gray-400 pb-2 border-b border-gray-700">
            <Building2 className="h-4 w-4 text-yellow-500" />
            <span>{equipment.business.business_name}</span>
            {equipment.business.average_rating && (
              <div className="flex items-center gap-1 ml-auto">
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                <span>{equipment.business.average_rating}</span>
              </div>
            )}
          </div>
        )}
        <div className="flex items-center text-sm text-gray-400">
          <Package className="h-4 w-4 mr-2 text-yellow-500" />
          {equipment?.quantity_available || 0} units available
        </div>
        <div className="pt-3 border-t border-gray-700">
          <div className="flex items-baseline">
            <span className="text-2xl font-bold text-yellow-500">{formatCurrencySimple(equipment?.price || 0)}</span>
            <span className="text-gray-500 ml-2">/{equipment?.price_unit || 'per day'}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button
          className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold transition-all"
          onClick={() => navigate(`/equipment/${equipment?.equipment_id}`)}
          data-testid={`view-equipment-btn-${equipment?.equipment_id}`}
        >
          View Details
        </Button>
        <Button
          variant={inCart ? "outline" : "default"}
          className={inCart 
            ? "border-green-500 text-green-500 hover:bg-green-900/20" 
            : "bg-gray-700 hover:bg-gray-600 text-white"
          }
          onClick={handleAddToCart}
          disabled={inCart}
          data-testid={`add-to-cart-btn-${equipment?.equipment_id}`}
        >
          {inCart ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default EquipmentCard;
